import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImg from '../../Images/logo_img_small.png';
import { getLogoBase64 } from '../../utils/logoBase64';
import { usesUnifiedDates, getDatesForPDF, getDateLabelForPDF } from '../../utils/unifiedDateStorage';

// Company branding
const BRAND_COLOR = '#a20000';
const BRAND_COLOR_RGB = [162, 0, 0];

// Company information
const COMPANY_INFO = {
  name: 'Beema Scaffolding (Singapore) Pte Ltd',
  address: '102F Pasir Panjang Road, #04-12',
  address2: 'Citilink Warehouse Complex',
  address3: 'Singapore 118530',
  phone: '+65 85123494',
  website: 'beemascaffolding.sg',
  email: 'contact@beemascaffolding.sg',
  uen: '202340913H'
};

const CONTACT_INFO = {
  name: 'Lina',
  phone: '+65 85123494',
  email: 'contact@beemascaffolding.sg'
};

// Payment details as requested
const PAYMENT_DETAILS = {
  damageLossPrices: {
    title: 'Damage/Loss Prices Per Item (For Rental)',
    items: [
      'Frame / Narrow Braces / Platform / Stairway: $380',
      'Guardrail / Handrail / Horizontal & Diagonal Braces: $170',
      'Wheels / Outrigger: $250',
      'Toe Board / Jack Base: $80',
      'Safety Clip: $20'
    ]
  },
  bankingDetails: {
    beneficiaryName: 'Beema Scaffolding Singapore Pte. Ltd.',
    beneficiaryBank: 'OCBC Current Account',
    accountNumber: '595853938001',
    paynowUEN: '202340913H'
  }
};

/**
 * Load and convert image to base64 with optimization
 * @param {string} imagePath - Path to image
 * @returns {Promise<string>} Base64 image data
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Optimize canvas size for PDF (smaller dimensions)
      const maxWidth = 150;  // Reduced from original size
      const maxHeight = 100; // Reduced from original size
      
      let { width, height } = img;
      
      // Scale down if image is too large
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Fill canvas with white background to prevent black background in JPEG
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      
      // Use better image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Use JPEG format with 70% quality for much smaller file size
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

/**
 * Generate and download invoice PDF
 * @param {Object} invoiceData - Invoice data
 */
export const generateInvoicePDF = async (invoiceData) => {
  try {
    console.log('Starting enhanced invoice PDF generation...');
    
    // Validate required data
    if (!invoiceData) {
      throw new Error('Invoice data is required');
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const footerHeight = 15; // Reserve space for footer
    const safeAreaBottom = pageHeight - footerHeight;
    let currentY = margin;

    // Page management functions
    const checkPageBreak = (requiredSpace = 10) => {
      if (currentY + requiredSpace > safeAreaBottom) {
        addFooter();
        pdf.addPage();
        currentY = margin;
        return true;
      }
      return false;
    };

    const addFooter = (pageNum = null, totalPagesCount = null) => {
      const footerY = pageHeight - 10;
      const currentPage = pageNum || pdf.internal.getCurrentPageInfo().pageNumber;
      const totalPages = totalPagesCount || pdf.internal.getNumberOfPages();
      
      // Footer separator line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      // Footer content
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('If you have any questions about this invoice, please contact:', margin, footerY);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${CONTACT_INFO.name}, ${CONTACT_INFO.phone}, ${CONTACT_INFO.email}`, margin, footerY + 6);
      
      // Page numbering (right aligned)
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, footerY + 6, { align: 'right' });
    };

    // Function to add footers to all pages after content is complete
    const addFootersToAllPages = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i, totalPages);
      }
    };

    // Function to calculate the height needed for payment details
    const calculatePaymentDetailsHeight = () => {
      const columnWidth = 85;
      let maxHeight = 0;
      
      // Calculate left column height (Damage/Loss Prices)
      let leftHeight = 8; // Title height
      PAYMENT_DETAILS.damageLossPrices.items.forEach(item => {
        const itemLines = pdf.splitTextToSize(`• ${item}`, columnWidth);
        leftHeight += itemLines.length * 4;
      });
      
      // Calculate right column height (Pay To)
      let rightHeight = 8; // Title height
      const bankingLines = [
        `Beneficiary Name: ${PAYMENT_DETAILS.bankingDetails.beneficiaryName}`,
        `Beneficiary Bank: ${PAYMENT_DETAILS.bankingDetails.beneficiaryBank}`,
        `Account Number: ${PAYMENT_DETAILS.bankingDetails.accountNumber}`,
        `Paynow UEN: ${PAYMENT_DETAILS.bankingDetails.paynowUEN}`
      ];
      
      bankingLines.forEach(line => {
        const wrappedLines = pdf.splitTextToSize(line, columnWidth);
        rightHeight += wrappedLines.length * 4;
      });
      
      maxHeight = Math.max(leftHeight, rightHeight);
      return maxHeight + 10; // Add some padding
    };

    // Function to add payment details with intelligent positioning
    const addPaymentDetailsToLastPage = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      pdf.setPage(totalPages); // Go to last page
      
      // Calculate the height needed for payment details
      const paymentDetailsHeight = calculatePaymentDetailsHeight();
      
      // Calculate the minimum Y position needed (above footer with padding)
      const minPaymentDetailsY = pageHeight - footerHeight - paymentDetailsHeight - 2; // 2mm padding above footer (reduced from 5mm)
      
      // Check if there's enough space on current page
      // We need to account for the current content position
      const availableSpace = safeAreaBottom - currentY;
      const needsNewPage = availableSpace < paymentDetailsHeight + 10; // 10mm buffer
      
      let paymentDetailsY;
      
      if (needsNewPage) {
        // Add a new page for payment details
        pdf.addPage();
        paymentDetailsY = margin + 20; // Start with some top margin on new page
      } else {
        // Use current page, position above footer
        paymentDetailsY = Math.max(currentY + 10, minPaymentDetailsY);
      }
      
      const columnWidth = 85; // Width for each column
      const columnSpacing = 5; // Spacing between columns
      
      // Left column - Damage/Loss Prices
      const leftColumnX = margin;
      const rightColumnX = margin + columnWidth + columnSpacing;
      
      // Left column content
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(PAYMENT_DETAILS.damageLossPrices.title, leftColumnX, paymentDetailsY);
      
      let leftContentY = paymentDetailsY + 8;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      
      PAYMENT_DETAILS.damageLossPrices.items.forEach(item => {
        const itemLines = pdf.splitTextToSize(`• ${item}`, columnWidth);
        itemLines.forEach(line => {
          pdf.text(line, leftColumnX, leftContentY);
          leftContentY += 4;
        });
      });
      
      // Right column content
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Pay To:', rightColumnX, paymentDetailsY);
      
      let rightContentY = paymentDetailsY + 8;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      
      const bankingLines = [
        `Beneficiary Name: ${PAYMENT_DETAILS.bankingDetails.beneficiaryName}`,
        `Beneficiary Bank: ${PAYMENT_DETAILS.bankingDetails.beneficiaryBank}`,
        `Account Number: ${PAYMENT_DETAILS.bankingDetails.accountNumber}`,
        `Paynow UEN: ${PAYMENT_DETAILS.bankingDetails.paynowUEN}`
      ];
      
      bankingLines.forEach(line => {
        const wrappedLines = pdf.splitTextToSize(line, columnWidth);
        wrappedLines.forEach(wrappedLine => {
          pdf.text(wrappedLine, rightColumnX, rightContentY);
          rightContentY += 4;
        });
      });
    };

    // Function to add footers to all pages and payment details to last page
    const addFootersAndPaymentDetailsToAllPages = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i, totalPages);
      }
      // Add payment details to last page
      addPaymentDetailsToLastPage();
    };

    // Helper function to add text with better positioning
    const addText = (text, x, y, options = {}) => {
      const {
        fontSize = 10,
        fontStyle = 'normal',
        align = 'left',
        maxWidth = pageWidth - 2 * margin,
        color = [0, 0, 0]
      } = options;

      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', fontStyle);
      pdf.setTextColor(color[0], color[1], color[2]);

      if (align === 'center') {
        pdf.text(text || '', pageWidth / 2, y, { align: 'center' });
      } else if (align === 'right') {
        pdf.text(text || '', x, y, { align: 'right' });
      } else {
        pdf.text(text || '', x, y);
      }
      
      return y + (fontSize * 0.35) + 2;
    };

    // Load and add logo with preserved aspect ratio using new utility
    let logoLoaded = false;
    
    try {
      console.log('Loading logo using utility function...');
      
      // Use the new logo utility function
      const logoBase64 = await getLogoBase64();
      
      // Create a temporary image to get original dimensions
      const tempImg = new Image();
      tempImg.src = logoBase64;
      await new Promise((resolve, reject) => {
        tempImg.onload = resolve;
        tempImg.onerror = reject;
        // Add timeout to prevent hanging
        setTimeout(reject, 3000);
      });
      
      // Calculate dimensions to preserve aspect ratio
      const maxWidth = 30;
      const maxHeight = 20;
      const aspectRatio = tempImg.width / tempImg.height;
      
      let logoWidth, logoHeight;
      if (aspectRatio > maxWidth / maxHeight) {
        // Image is wider, constrain by width
        logoWidth = maxWidth;
        logoHeight = maxWidth / aspectRatio;
      } else {
        // Image is taller, constrain by height
        logoHeight = maxHeight;
        logoWidth = maxHeight * aspectRatio;
      }
      
      pdf.addImage(logoBase64, 'JPEG', margin, currentY, logoWidth, logoHeight);
      console.log('Logo loaded successfully using utility function');
      logoLoaded = true;
    } catch (error) {
      console.error('Could not load logo using utility function:', error);
      
      // Final fallback: try the original method
      try {
        console.log('Trying final fallback logo loading...');
        const logoBase64 = await loadImageAsBase64(logoImg);
        
        const tempImg = new Image();
        tempImg.src = logoBase64;
        await new Promise((resolve, reject) => {
          tempImg.onload = resolve;
          tempImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const maxWidth = 30;
        const maxHeight = 20;
        const aspectRatio = tempImg.width / tempImg.height;
        
        let logoWidth, logoHeight;
        if (aspectRatio > maxWidth / maxHeight) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / aspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * aspectRatio;
        }
        
        pdf.addImage(logoBase64, 'JPEG', margin, currentY, logoWidth, logoHeight);
        console.log('Logo loaded successfully from final fallback method');
        logoLoaded = true;
      } catch (fallbackError) {
        console.error('Final fallback logo loading also failed:', fallbackError);
      }
    }

    if (!logoLoaded) {
      console.error('Failed to load logo. PDF will continue without logo.');
    }

    // HEADER SECTION - Company name next to logo, INVOICE on right
    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY_INFO.name, margin + 32, currentY + 8);
    pdf.setFontSize(11);
    pdf.text('INVOICE', pageWidth - margin, currentY + 18, { align: 'right' });
    
    currentY += 25;

    // Company address block (smaller font)
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    currentY = addText(COMPANY_INFO.address, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address2, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address3, margin, currentY, { fontSize: 8 });
    currentY += 1;
    currentY = addText(`Phone: ${COMPANY_INFO.phone}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Website: ${COMPANY_INFO.website}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Email: ${COMPANY_INFO.email}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`UEN: ${COMPANY_INFO.uen}`, margin, currentY, { fontSize: 8 });

    // Date and invoice number (properly right aligned with consistent spacing)
    const dateInfoY = margin + 25;
    addText(`DATE: ${invoiceData.createdAt?.toLocaleDateString('en-GB') || new Date().toLocaleDateString('en-GB')}`, pageWidth - margin, dateInfoY, { 
      fontSize: 10, 
      align: 'right' 
    });
    addText(`INVOICE #: ${invoiceData.invoiceNumber || 'DRAFT'}`, pageWidth - margin, dateInfoY + 7, { 
      fontSize: 10, 
      align: 'right' 
    });
    
    // Add reference invoice below invoice number for damaged items
    let nextLineY = dateInfoY + 14;
    if (invoiceData.invoiceType === 'damaged_items' && invoiceData.originalInvoiceNumber) {
      addText(`REF: ${invoiceData.originalInvoiceNumber}`, pageWidth - margin, nextLineY, { 
        fontSize: 10, 
        align: 'right' 
      });
      nextLineY += 7;
    }
    
    if (invoiceData.dueDate) {
      addText(`DUE DATE: ${invoiceData.dueDate}`, pageWidth - margin, nextLineY, { 
        fontSize: 10, 
        align: 'right' 
      });
    }

  // Add horizontal line separator before client and quotation details
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;

    // TWO-COLUMN LAYOUT FOR CUSTOMER AND JOB DETAILS
    const leftColX = margin;
    const rightColX = pageWidth / 2 + 10;
    const sectionStartY = currentY;

    // LEFT COLUMN - Customer Details
    
    let leftY = sectionStartY + 2;
    
    // Set consistent font settings for all customer information
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    
    // Company/Customer name
    const customerName = invoiceData.companyName || invoiceData.customerName || 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('To:', leftColX, leftY);
    const toLabelWidth = pdf.getTextWidth('To: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(customerName, leftColX + toLabelWidth, leftY);
    leftY += 6;
    
    
    // Attention - customer name if company exists, otherwise N/A
    const attnText = invoiceData.companyName ? (invoiceData.attn || 'N/A') : 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('Attn:', leftColX, leftY);
    const attnLabelWidth = pdf.getTextWidth('Attn: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(attnText, leftColX + attnLabelWidth, leftY);
    leftY += 6;
    
    // Phone - enhanced fix for character spacing issue
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    
    // Render "Phone:" label first in bold
    pdf.text('Phone:', leftColX, leftY);
    
    // Calculate width of "Phone: " to position the value correctly
    const phoneLabel = 'Phone: ';
    const phoneLabelWidth = pdf.getTextWidth(phoneLabel);
    
    // Switch to normal font for the value
    pdf.setFont('helvetica', 'normal');
    
    // Enhanced phone value cleaning and rendering
    const phoneValue = invoiceData.tel || 'N/A';
    
    // Aggressive data sanitization
    let cleanPhoneValue = phoneValue.toString()
      .trim()
      .normalize('NFD') // Unicode normalization
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/[^\x20-\x7E]/g, '') // Keep only printable ASCII characters
      .trim();
    
    // If still having issues, try character-by-character rendering
    try {
      // First try normal rendering
      pdf.text(cleanPhoneValue, leftColX + phoneLabelWidth, leftY);
    } catch (error) {
      console.warn('Normal phone rendering failed, trying character-by-character:', error);
      // Fallback: render character by character
      let currentX = leftColX + phoneLabelWidth;
      for (let i = 0; i < cleanPhoneValue.length; i++) {
        const char = cleanPhoneValue[i];
        pdf.text(char, currentX, leftY);
        currentX += pdf.getTextWidth(char);
      }
    }
    leftY += 6;
    
    // Email - ensure consistent font settings
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Email:', leftColX, leftY);
    const emailLabelWidth = pdf.getTextWidth('Email: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(invoiceData.email || 'N/A', leftColX + emailLabelWidth, leftY);
    leftY += 6;
    
    // Billing Address (only if it has a value)
    if (invoiceData.billingAddress && invoiceData.billingAddress.trim() !== '') {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Billing Address:', leftColX, leftY);
      const billingAddressLabelWidth = pdf.getTextWidth('Billing Address: ');
      pdf.setFont('helvetica', 'normal');
      
      // Split the billing address text and handle multi-line display
      const billingAddressText = invoiceData.billingAddress;
      const availableWidth = (pageWidth / 2) - 10 - billingAddressLabelWidth;
      const billingAddressLines = pdf.splitTextToSize(billingAddressText, availableWidth);
      
      // First line goes next to the label
      if (billingAddressLines.length > 0) {
        pdf.text(billingAddressLines[0], leftColX + billingAddressLabelWidth, leftY);
        leftY += 6;
        
        // Additional lines go on new lines with proper indentation
        for (let i = 1; i < billingAddressLines.length; i++) {
          pdf.text(billingAddressLines[i], leftColX + billingAddressLabelWidth, leftY);
          leftY += 6;
        }
      }
    }
    


    // RIGHT COLUMN - Invoice Details
    let rightY = sectionStartY + 2;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    // Invoice Type
    pdf.setFont('helvetica', 'bold');
    pdf.text('Invoice Type:', rightColX, rightY);
    const invoiceTypeLabelWidth = pdf.getTextWidth('Invoice Type: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(getInvoiceTypeLabel(invoiceData.invoiceType), rightColX + invoiceTypeLabelWidth, rightY);
    rightY += 6;
    
    // Site Address (only if it has a value) - with proper text wrapping like quotation PDF
    if (invoiceData.siteAddress && invoiceData.siteAddress.trim() !== '') {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Site Address:', rightColX, rightY);
      const siteAddressLabelWidth = pdf.getTextWidth('Site Address: ');
      pdf.setFont('helvetica', 'normal');
      
      // Split the address text and handle multi-line display
      const siteAddressText = invoiceData.siteAddress;
      const availableWidth = (pageWidth / 2) - 20 - siteAddressLabelWidth;
      const siteAddressLines = pdf.splitTextToSize(siteAddressText, availableWidth);
      
      // First line goes next to the label
      if (siteAddressLines.length > 0) {
        pdf.text(siteAddressLines[0], rightColX + siteAddressLabelWidth, rightY);
        rightY += 6;
        
        // Additional lines go on new lines with proper indentation
        for (let i = 1; i < siteAddressLines.length; i++) {
          pdf.text(siteAddressLines[i], rightColX + siteAddressLabelWidth, rightY);
          rightY += 6;
        }
      }
    }
    
    // Display date information using unified date system
    if (usesUnifiedDates(invoiceData)) {
      const datesForPDF = getDatesForPDF(invoiceData);
      if (datesForPDF) {
        const dateLabel = getDateLabelForPDF(invoiceData);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${dateLabel}:`, rightColX, rightY);
        const datesLabelWidth = pdf.getTextWidth(`${dateLabel}: `);
        pdf.setFont('helvetica', 'normal');

        // Handle multi-line dates
        const availableWidth = (pageWidth / 2) - 20 - datesLabelWidth;
        const datesLines = pdf.splitTextToSize(datesForPDF, availableWidth);

        // First line goes next to the label
        if (datesLines.length > 0) {
          pdf.text(datesLines[0], rightColX + datesLabelWidth, rightY);
          rightY += 6;

          // Additional lines go on new lines with proper indentation
          for (let i = 1; i < datesLines.length; i++) {
            pdf.text(datesLines[i], rightColX + datesLabelWidth, rightY);
            rightY += 6;
          }
        }
      }
    } else {
      // Fallback to legacy date system for backward compatibility
      if (invoiceData.invoiceType === 'rental' || invoiceData.invoiceType === 'rental_extension') {
        // Rental type - show rental period or duration
        if (invoiceData.rentalInputType === 'duration' && invoiceData.rentalDuration && invoiceData.rentalDurationUnit) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Rental Duration:', rightColX, rightY);
          const rentalDurationLabelWidth = pdf.getTextWidth('Rental Duration: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${invoiceData.rentalDuration} ${invoiceData.rentalDurationUnit}`, rightColX + rentalDurationLabelWidth, rightY);
          rightY += 6;
        } else if (invoiceData.rentalInputType === 'date' && invoiceData.rentalStartDate && invoiceData.rentalEndDate) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Rental Period:', rightColX, rightY);
          const rentalPeriodLabelWidth = pdf.getTextWidth('Rental Period: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${invoiceData.rentalStartDate} to ${invoiceData.rentalEndDate}`, rightColX + rentalPeriodLabelWidth, rightY);
          rightY += 6;
        }
      } else if (invoiceData.invoiceType === 'purchase') {
          // Purchase type - show delivery date, lead time, or TBC
          if (invoiceData.leadTimeInputType === 'date' && invoiceData.leadTime && invoiceData.leadTime !== 'TBC') {
            pdf.setFont('helvetica', 'bold');
            pdf.text('Delivery Date:', rightColX, rightY);
            const deliveryDateLabelWidth = pdf.getTextWidth('Delivery Date: ');
            pdf.setFont('helvetica', 'normal');
            pdf.text(invoiceData.leadTime, rightColX + deliveryDateLabelWidth, rightY);
            rightY += 6;
          } else if ((invoiceData.leadTimeInputType === 'months' || invoiceData.leadTimeInputType === 'duration') && invoiceData.leadTimeMonths) {
            pdf.setFont('helvetica', 'bold');
            pdf.text('Lead Time:', rightColX, rightY);
            const leadTimeLabelWidth = pdf.getTextWidth('Lead Time: ');
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${invoiceData.leadTimeMonths} month(s)`, rightColX + leadTimeLabelWidth, rightY);
            rightY += 6;
          } else if (invoiceData.leadTimeInputType === 'tbc' || invoiceData.leadTime === 'TBC') {
            pdf.setFont('helvetica', 'bold');
            pdf.text('Delivery Date:', rightColX, rightY);
            const deliveryDateTbcLabelWidth = pdf.getTextWidth('Delivery Date: ');
            pdf.setFont('helvetica', 'normal');
            pdf.text('TBC', rightColX + deliveryDateTbcLabelWidth, rightY);
            rightY += 6;
          }
      } else if (invoiceData.invoiceType === 'scaffolding_service') {
        // Scaffolding service type - show service date, date range, or TBC
        if (invoiceData.serviceInputType === 'date' && invoiceData.serviceDate) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Service Date:', rightColX, rightY);
          const serviceDateLabelWidth = pdf.getTextWidth('Service Date: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text(invoiceData.serviceDate, rightColX + serviceDateLabelWidth, rightY);
          rightY += 6;
        } else if (invoiceData.serviceInputType === 'range' && invoiceData.serviceStartDate && invoiceData.serviceEndDate) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Service Dates:', rightColX, rightY);
          const serviceDatesLabelWidth = pdf.getTextWidth('Service Dates: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${invoiceData.serviceStartDate} to ${invoiceData.serviceEndDate}`, rightColX + serviceDatesLabelWidth, rightY);
          rightY += 6;
        } else if (invoiceData.serviceInputType === 'tbc') {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Service Date:', rightColX, rightY);
          const serviceDateTbcLabelWidth = pdf.getTextWidth('Service Date: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text('TBC', rightColX + serviceDateTbcLabelWidth, rightY);
          rightY += 6;
        }
      } else if (invoiceData.invoiceType === 'damaged_items') {
        // Damaged Items type - show collection date only (original invoice reference is shown in header)
        if (invoiceData.collectionDate) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Collection Date:', rightColX, rightY);
          const collectionDateLabelWidth = pdf.getTextWidth('Collection Date: ');
          pdf.setFont('helvetica', 'normal');
          pdf.text(invoiceData.collectionDate, rightColX + collectionDateLabelWidth, rightY);
          rightY += 6;
        }
      }

      // Legacy service dates (for backward compatibility) - only show if no serviceInputType is set
      if (!invoiceData.serviceInputType && (invoiceData.serviceStartDate || invoiceData.serviceEndDate)) {
        if (invoiceData.serviceStartDate && invoiceData.serviceEndDate) {
          pdf.text(`Service Period: ${invoiceData.serviceStartDate} to ${invoiceData.serviceEndDate}`, rightColX, rightY);
          rightY += 6;
        } else if (invoiceData.serviceStartDate) {
          pdf.text(`Service Start: ${invoiceData.serviceStartDate}`, rightColX, rightY);
          rightY += 6;
        } else if (invoiceData.serviceEndDate) {
          pdf.text(`Service End: ${invoiceData.serviceEndDate}`, rightColX, rightY);
          rightY += 6;
        }
      }
    }
    
    // PO Number
    if (invoiceData.poNumber) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`PO Number: `, rightColX, rightY);
      const poNumLabelWidth = pdf.getTextWidth('PO Number: ');
      pdf.setFont('helvetica', 'normal');
      pdf.text(invoiceData.poNumber, rightColX + poNumLabelWidth, rightY);
      rightY += 6;
    }
    
    // Payment Terms
    pdf.setFont('helvetica', 'bold');
    pdf.text('Payment Terms:', rightColX, rightY);
    const paymentTermsLabelWidth = pdf.getTextWidth('Payment Terms: ');
    pdf.setFont('helvetica', 'normal');
    
    let paymentTermsValue = '';
    if (invoiceData.customPaymentTerms) {
      paymentTermsValue = invoiceData.customPaymentTerms;
    } else {
      paymentTermsValue = invoiceData.paymentTerms || '';
    }
    
    // Handle multi-line payment terms
    const availableWidth = (pageWidth / 2) - 20 - paymentTermsLabelWidth;
    const paymentTermsLines = pdf.splitTextToSize(paymentTermsValue, availableWidth);
    
    // First line goes next to the label
    if (paymentTermsLines.length > 0) {
      pdf.text(paymentTermsLines[0], rightColX + paymentTermsLabelWidth, rightY);
      rightY += 6;
      
      // Additional lines go on new lines with proper indentation
      for (let i = 1; i < paymentTermsLines.length; i++) {
        pdf.text(paymentTermsLines[i], rightColX + paymentTermsLabelWidth, rightY);
        rightY += 6;
      }
    }

    currentY = Math.max(leftY, rightY) + 3;

    // SERVICES/PRODUCTS TABLE using autoTable for proper multi-page handling
    if (invoiceData.lineItems && Array.isArray(invoiceData.lineItems) && invoiceData.lineItems.length > 0) {
      const lineItems = invoiceData.lineItems.filter(item => item && item.description && item.description.trim() !== '');
      
      if (lineItems.length > 0) {
        // Prepare table data
        const tableData = lineItems.map((item, index) => [
          (index + 1).toString(),
          item.description || '',
          (item.quantity || 0).toString(),
          `$${(item.unitPrice || 0).toFixed(2)}`,
          `$${(item.total || 0).toFixed(2)}`
        ]);

        // Configure autoTable
        autoTable(pdf, {
          startY: currentY,
          head: [['#', 'Description', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: BRAND_COLOR_RGB,
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
            halign: 'left'
          },
          bodyStyles: {
            fontSize: 9,
            textColor: [0, 0, 0],
            halign: 'left'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' }, // #
            1: { cellWidth: 111, halign: 'left' },  // Description
            2: { cellWidth: 20, halign: 'center' }, // Qty
            3: { cellWidth: 20, halign: 'right' },  // Unit Price
            4: { cellWidth: 20, halign: 'right' }   // Total
          },
          margin: { left: margin, right: margin },
          tableWidth: 186,
          styles: {
            cellPadding: 2,
            lineColor: [0, 0, 0],
            lineWidth: 0.1
          },
          // Handle page breaks properly
          didDrawPage: function(data) {
            // This ensures proper spacing from footer
            const pageHeight = pdf.internal.pageSize.getHeight();
            if (data.cursor.y > pageHeight - footerHeight - 10) {
              // Move to next page if too close to footer area
              return false;
            }
          },
          // Reserve space for footer
          pageBreak: 'auto',
          showFoot: 'everyPage',
          margin: { 
            left: margin, 
            right: margin, 
            top: margin, 
            bottom: footerHeight + 5 
          }
        });

        // Update currentY to position after the table
        currentY = pdf.lastAutoTable.finalY + 10;
      }
    }

    // TWO-COLUMN LAYOUT: NOTES ON LEFT, PRICING ON RIGHT
    const notesColumnWidth = 90; // Left column width for notes
    const pricingColumnX = pageWidth - 60; // Right column start position for pricing
    const notesPricingSectionStartY = currentY;
    
    let notesY = notesPricingSectionStartY;
    let pricingY = notesPricingSectionStartY;
    
    // LEFT COLUMN - Additional Notes (if they exist)
    if (invoiceData.notes && invoiceData.notes.trim() !== '') {
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Additional Notes:', margin, notesY);
      notesY += 8;
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const noteLines = pdf.splitTextToSize(invoiceData.notes, notesColumnWidth);
      noteLines.forEach(line => {
        pdf.text(line, margin, notesY);
        notesY += 5;
      });
    }
    
    // RIGHT COLUMN - Pricing Section
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Subtotal: $${(invoiceData.subtotal || 0).toFixed(2)}`, pageWidth - margin, pricingY, { align: 'right' });
    pricingY += 8;
    
    // Discount (if applicable)
    if (invoiceData.discount && invoiceData.discount.value > 0) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const discountAmount = invoiceData.discountType === 'percentage' 
        ? (invoiceData.subtotal * invoiceData.discount.value / 100)
        : invoiceData.discount.value;
      pdf.text(`Discount: -$${discountAmount.toFixed(2)}`, pageWidth - margin, pricingY, { align: 'right' });
      pricingY += 8;
    }
    
    // Grand Total with line
    pdf.setDrawColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setLineWidth(0.7);
    pdf.line(pageWidth - 60, pricingY - 2, pageWidth - margin, pricingY - 2);
    
    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`TOTAL (SGD): $${(invoiceData.grandTotal || 0).toFixed(2)}`, pageWidth - margin, pricingY + 5, { align: 'right' });
    
    pdf.line(pageWidth - 60, pricingY + 10, pageWidth - margin, pricingY + 10);
    pricingY += 15;
    
    // Update currentY to the maximum of both columns
    currentY = Math.max(notesY, pricingY) + 5;

    // Add footers to all pages with correct page numbering and payment details on last page
    addFootersAndPaymentDetailsToAllPages();

    // Generate filename
    const companyName = (invoiceData.companyName || invoiceData.customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const invoiceNumber = invoiceData.invoiceNumber || 'DRAFT';
    const fileName = `${invoiceNumber}.pdf`;
    
    console.log('Enhanced invoice PDF generated successfully');
    
    // Return PDF as blob for upload (instead of downloading)
    const pdfBlob = pdf.output('blob');
    console.log('PDF blob created, size:', pdfBlob.size, 'bytes');
    
    return pdfBlob;
    
  } catch (error) {
    console.error('Invoice PDF generation error:', error);
    throw new Error(`PDF generation failed: ${error.message}`);
  }
};

/**
 * Generate and download invoice PDF (for direct download functionality)
 * @param {Object} invoiceData - Invoice data
 */
export const downloadInvoicePDF = async (invoiceData) => {
  try {
    const pdfBlob = await generateInvoicePDF(invoiceData);
    
    // Generate filename
    const companyName = (invoiceData.companyName || invoiceData.customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const invoiceNumber = invoiceData.invoiceNumber || 'DRAFT';
    const fileName = `${invoiceNumber}.pdf`;
    
    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('Invoice PDF downloaded successfully');
    return true;
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  }
};

/**
 * Get display label for invoice type
 * @param {string} type - Invoice type
 * @returns {string} Display label
 */
const getInvoiceTypeLabel = (type) => {
  const labels = {
    'purchase': 'Purchase',
    'rental': 'Rental',
    'rental_extension': 'Rental Extension',
    'scaffolding_service': 'Scaffolding Service',
    'damaged_items': 'Damaged Items'
  };
  return labels[type] || type;
};

/**
 * Get display name for additional service
 * @param {string} serviceKey - Service key
 * @returns {string} Display name
 */
const getServiceDisplayName = (serviceKey) => {
  const names = {
    'additionalTransport': 'Additional transport of scaffold',
    'additionalInstallation': 'Additional installation / dismantling',
    'stairsInstallation': 'Installation of scaffold on stairs',
    'stairsLoading': 'Loading & unloading of scaffold via stairs',
    'weeklyInspection': 'Weekly Scaffold inspection and endorsement'
  };
  return names[serviceKey] || serviceKey;
};

export default generateInvoicePDF;
